import { asRecord, num, records, text } from './resultValues.js';

export const FEATURE_SCORE_MAX = 4;

const BRAND_GUIDELINES_SOURCE = 'brand guidelines';

export interface AnalysedFeature {
  feature: string;
  tag: string;
  score: number | null;
  performance: number | null;
  bestPractice: number | null;
  recommendation: string | null;
  fromBrandGuidelines: boolean;
}

export interface AnalysedAttribute {
  name: string;
  weightPercent: number;
  score: number | null;
  categoryMax: number;
  categoryScore: number;
  percentOfMax: number;
  groups: Array<{ abcd: string; features: AnalysedFeature[] }>;
}

export interface BrandGuidelineCheck {
  feature: string;
  tag: string;
  score: number | null;
}

export interface BrandGuidelines {
  score: number | null;
  scored: BrandGuidelineCheck[];
  unscored: BrandGuidelineCheck[];
}

function isBrandGuidelines(source: unknown): boolean {
  return text(source)?.trim().toLowerCase() === BRAND_GUIDELINES_SOURCE;
}

function scoredRow(
  result: unknown,
  creativeId: string,
  networkId: number,
): Record<string, unknown> | null {
  return (
    records(asRecord(result)?.['creatives']).find(
      (row) => text(row['creative_id']) === creativeId && num(row['network_id']) === networkId,
    ) ?? null
  );
}

function featureOf(row: Record<string, unknown>): AnalysedFeature | null {
  const feature = text(row['feature']);
  if (feature === null) return null;
  return {
    feature,
    tag: text(row['tag']) ?? '',
    score: num(row['final_feature_score']),
    performance: num(row['performance_score']),
    bestPractice: num(row['best_practice_score']),
    recommendation: text(row['recommendation']),
    fromBrandGuidelines: isBrandGuidelines(row['source']),
  };
}

export function analysedAttributes(
  result: unknown,
  creativeId: string,
  networkId: number,
): AnalysedAttribute[] {
  const row = scoredRow(result, creativeId, networkId);
  if (row === null) return [];
  const weights = asRecord(row['attribute_weights']) ?? {};
  const scores = asRecord(row['attribute_scores']) ?? {};
  const scoredFeatures = records(row['features']).filter(
    (entry) => num(entry['final_feature_score']) !== null,
  );

  return Object.keys(scores).flatMap((name) => {
    const mine = scoredFeatures.filter((entry) => text(entry['attribute']) === name);
    if (mine.length === 0) return [];
    const byMethodology = new Map<string, AnalysedFeature[]>();
    for (const entry of mine) {
      const analysed = featureOf(entry);
      if (analysed === null) continue;
      const abcd = text(entry['abcd_methodology']) ?? '';
      byMethodology.set(abcd, [...(byMethodology.get(abcd) ?? []), analysed]);
    }

    const weightPercent = Math.round((num(weights[name]) ?? 0) * 100);
    const rawMax = mine.length * FEATURE_SCORE_MAX;
    const rawSum = mine.reduce(
      (total, entry) => total + (num(entry['final_feature_score']) ?? 0),
      0,
    );
    const categoryMax = weightPercent / 10;
    return [
      {
        name,
        weightPercent,
        score: num(scores[name]),
        categoryMax,
        categoryScore: rawMax === 0 ? 0 : categoryMax * (rawSum / rawMax),
        percentOfMax: rawMax === 0 ? 0 : Math.round((rawSum / rawMax) * 100),
        groups: [...byMethodology.entries()]
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([abcd, features]) => ({ abcd, features })),
      },
    ];
  });
}

export function brandGuidelines(
  result: unknown,
  creativeId: string,
  networkId: number,
): BrandGuidelines {
  const row = scoredRow(result, creativeId, networkId);
  if (row === null) return { score: null, scored: [], unscored: [] };
  const scoreByFeature = new Map<string, number | null>(
    records(row['features']).map((entry) => [
      `${text(entry['feature'])}-${text(entry['tag'])}`,
      num(entry['brand_guidelines_score']),
    ]),
  );
  const checks = records(row['detections'])
    .filter((entry) => isBrandGuidelines(entry['source']))
    .flatMap((entry) => {
      const feature = text(entry['feature']);
      if (feature === null) return [];
      const tag = text(entry['tag']) ?? '';
      return [{ feature, tag, score: scoreByFeature.get(`${feature}-${tag}`) ?? null }];
    });

  return {
    score: num(row['brand_guidelines_overall_score']),
    scored: checks.filter((check) => check.score !== null),
    unscored: checks.filter((check) => check.score === null),
  };
}
